import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { X, Image as ImageIcon, Loader2 } from "lucide-react";

const API_BASE_URL = import.meta.env.VITE_API_URL;

const MAX_IMAGE_MB = 8;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

function validateImages(files) {
  for (const f of files) {
    if (!ALLOWED_TYPES.includes(f.type)) {
      throw new Error("Only JPG, PNG, or WEBP images are allowed.");
    }
    if (f.size > MAX_IMAGE_MB * 1024 * 1024) {
      throw new Error(
        `One of the images is too large. Max ${MAX_IMAGE_MB}MB each.`
      );
    }
  }
}

// Accepts either [id, id...] or [{id, url}, ...] and returns [id, ...]
function normalizeImageIds(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((it) => {
    if (it && typeof it === "object") {
      if ("id" in it) return it.id;
      if ("image_id" in it) return it.image_id;
      return it;
    }
    return it;
  });
}

function arraysEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// Build an <img> src from different shapes: string / {url} / {path}
function getImageSrc(img) {
  if (!img) return "";

  if (typeof img === "string") {
    if (img.startsWith("http")) return img;
    return `${API_BASE_URL}/${img.replace(/^\/+/, "")}`;
  }

  if (img.url) {
    if (img.url.startsWith("http")) return img.url;
    return `${API_BASE_URL}/${img.url.replace(/^\/+/, "")}`;
  }
  if (img.path) {
    if (img.path.startsWith("http")) return img.path;
    return `${API_BASE_URL}/${img.path.replace(/^\/+/, "")}`;
  }
  if (img.name) {
    if (img.name.startsWith("http")) return img.name;
    return `${API_BASE_URL}/${img.name.replace(/^\/+/, "")}`;
  }

  return "";
}

function getImageIdDisplay(img) {
  if (!img) return "";
  if (typeof img === "object") {
    return img.id ?? img.image_id ?? "";
  }
  return img;
}

const PREDEFINED_FEATURES = [
  "Wheelchair Access",
  "Emergency Call System",
  "Wide Doorways",
  "Support Rails",
  "Ramps",
  "Ground Floor Bedroom",
  "Modified Bathroom",
  "Lift Access",
  "Multiple Access Points",
  "Wide Corridors",
  "Therapy Room",
  "Private Entrance",
  "Open Plan",
];

const PREDEFINED_AMENITIES = [
  "Wi-Fi",
  "Air Conditioning",
  "Kitchen",
  "Laundry",
  "Garden",
  "Parking",
  "Living Room",
  "Sea View",
  "Security",
  "Dining Area",
  "Patio",
  "Storage",
  "Kitchenette",
  "Heating",
];

function isPendingFileItem(x) {
  return x && typeof x === "object" && x.__local === true;
}

function AccommodationModal({ open, onClose, accommodation, onSuccess }) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(accommodation);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [uploadingImages, setUploadingImages] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  const emptyForm = {
    title: "",
    location: "",
    capacity: "",
    description: "",
    accommodationType: "",
    gender: "",
    bedrooms: "",
    bathrooms: "",
    supportLevel: "",
    features: [],
    amenities: [],
    images: [], // can be ids, paths, or {id,url,path}
    status: "Vacant",
    listingStatus: "listed",
  };

  const [formData, setFormData] = useState(emptyForm);

  const initialImagesRef = useRef([]);
  const fileInputRef = useRef(null);

  // Reset / preload when modal opens
  useEffect(() => {
    if (!open) {
      try {
        pendingFiles.forEach((it) => {
          if (it && it.preview) URL.revokeObjectURL(it.preview);
        });
      } catch {}

      setErrorMsg("");
      setLoading(false);
      setUploadingImages(false);
      setPendingFiles([]);
      return;
    }

    if (accommodation) {
      const normalizedImages = normalizeImageIds(accommodation.images);
      initialImagesRef.current = normalizedImages.slice();

      setPendingFiles([]);

      setFormData({
        title: accommodation.title || "",
        location: accommodation.location || "",
        capacity:
          accommodation.capacity !== undefined &&
          accommodation.capacity !== null
            ? String(accommodation.capacity)
            : "",
        description: accommodation.description || "",
        accommodationType:
          accommodation.accommodationType ||
          accommodation.accommodation_type ||
          "",
        gender: accommodation.gender || "",
        bedrooms:
          accommodation.bedrooms !== undefined &&
          accommodation.bedrooms !== null
            ? String(accommodation.bedrooms)
            : "",
        bathrooms:
          accommodation.bathrooms !== undefined &&
          accommodation.bathrooms !== null
            ? String(accommodation.bathrooms)
            : "",
        supportLevel:
          accommodation.supportLevel || accommodation.support_level || "",
        features: Array.isArray(accommodation.features)
          ? accommodation.features
          : [],
        amenities: Array.isArray(accommodation.amenities)
          ? accommodation.amenities
          : [],
        images: Array.isArray(accommodation.images) ? accommodation.images : [],
        status: accommodation.status || "Vacant",
        listingStatus:
          accommodation.listing_status ||
          accommodation.listingStatus ||
          "listed",
      });
    } else {
      initialImagesRef.current = [];
      setPendingFiles([]);
      setFormData(emptyForm);
    }
  }, [accommodation, open]);

  const toggleFeature = (feature) => {
    setFormData((prev) => {
      const updated = prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature];
      return { ...prev, features: updated };
    });
  };

  const toggleAmenity = (amenity) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  // ✅ FIX: This was missing in your file. Also made it more reliable:
  // - validates type/size
  // - uploads in parallel with Promise.allSettled
  // - shows partial success instead of failing everything
  // - clears input so you can select the same file again
  const handleImageFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // so you can pick the same file again
    if (fileInputRef.current) fileInputRef.current.value = "";

    setErrorMsg("");

    try {
      validateImages(files);

      // Store selected images locally (NOT uploaded yet)
      const localItems = files.map((file) => ({
        __local: true,
        file,
        preview: URL.createObjectURL(file),
      }));

      setPendingFiles((prev) => [...prev, ...localItems]);
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  const removeImage = (image) => {
    // If it's a pending (local) image, remove from pendingFiles
    if (isPendingFileItem(image)) {
      try {
        URL.revokeObjectURL(image.preview);
      } catch {}

      setPendingFiles((prev) => prev.filter((x) => x !== image));
      return;
    }

    // Otherwise it's an already-uploaded image from DB
    setFormData((prev) => {
      const targetId =
        typeof image === "object" ? image.id ?? image.image_id : image;

      return {
        ...prev,
        images: (prev.images || []).filter((img) => {
          const imgId = typeof img === "object" ? img.id ?? img.image_id : img;
          return imgId !== targetId;
        }),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);

    const existingCount = (formData.images || []).length;
    const pendingCount = pendingFiles.length;
    const imageCount = existingCount + pendingCount;

    if (imageCount < 5) {
      setErrorMsg(
        "Please upload at least 5 images for this accommodation before saving."
      );
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      setErrorMsg("Not authenticated. Please log in again.");
      setLoading(false);
      return;
    }

    let uploadedIds = [];

    if (pendingFiles.length > 0) {
      setUploadingImages(true);

      const uploadOne = async (file) => {
        const fd = new FormData();
        fd.append("file", file);

        const res = await fetch(`${API_BASE_URL}/api/sdaowner/upload_image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });

        let data = {};
        try {
          data = await res.json();
        } catch (e) {
          data = {};
        }

        if (!res.ok) {
          throw new Error(data.message || `Failed to upload ${file.name}`);
        }

        const returnedId =
          data.id ??
          data.image_id ??
          data.imageId ??
          (data.image && (data.image.id ?? data.image.image_id)) ??
          (data.data && (data.data.id ?? data.data.image_id)) ??
          null;

        if (!returnedId) {
          console.error("upload_image response (no id):", data);
          throw new Error(
            "Upload succeeded but server did not return an image id."
          );
        }

        // keep full shape so we can preview from server too
        return {
          id: returnedId,
          path: data.path || "",
          url: data.url || "",
          name: data.name || "",
        };
      };

      let uploadedItems = [];

      try {
        uploadedItems = await Promise.all(
          pendingFiles.map((it) => uploadOne(it.file))
        );
      } catch (err) {
        setErrorMsg(err.message);
        setUploadingImages(false);
        setLoading(false);
        return;
      }

      uploadedIds = uploadedItems
        .map((it) => it.id)
        .filter((id) => id !== null && id !== undefined);

      setUploadingImages(false);
    }

    const url = isEdit
      ? `${API_BASE_URL}/api/sdaowner/update_accommodation/${accommodation.id}`
      : `${API_BASE_URL}/api/sdaowner/add_accommodation`;

    const method = isEdit ? "PUT" : "POST";

    const normalizedCurrentImages = normalizeImageIds(formData.images).filter(
      (id) => id !== null && id !== undefined
    );

    const finalImageIds = Array.from(
      new Set([...normalizedCurrentImages, ...uploadedIds])
    );

    if (finalImageIds.length < 5) {
      setErrorMsg(
        "Some images failed to upload. Please make sure you have at least 5 images."
      );
      setLoading(false);
      return;
    }

    const basePayload = {
      title: formData.title,
      location: formData.location,
      capacity: formData.capacity === "" ? 0 : Number(formData.capacity),
      description: formData.description,
      accommodationType: formData.accommodationType,
      gender: formData.gender,
      bedrooms: formData.bedrooms === "" ? 0 : Number(formData.bedrooms),
      bathrooms: formData.bathrooms === "" ? 0 : Number(formData.bathrooms),
      supportLevel: formData.supportLevel,
      features: Array.isArray(formData.features) ? formData.features : [],
      amenities: Array.isArray(formData.amenities) ? formData.amenities : [],
      status: formData.status,
      listing_status: formData.listingStatus || "listed",
    };

    const payload = { ...basePayload };
    if (!isEdit) {
      payload.images = finalImageIds;
    } else {
      const same = arraysEqual(initialImagesRef.current, finalImageIds);
      if (!same) payload.images = finalImageIds;
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            (isEdit
              ? "Failed to update accommodation"
              : "Failed to add accommodation")
        );
      }

      if (onSuccess) onSuccess();

      // ✅ refresh caches so Availability + Dashboard reflect updates without refresh
      queryClient.invalidateQueries({ queryKey: ["sdaowner-accommodations"] });
      queryClient.invalidateQueries({ queryKey: ["accommodations"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });

      // cleanup previews + pending list
      try {
        pendingFiles.forEach((it) => {
          if (it.preview) URL.revokeObjectURL(it.preview);
        });
      } catch {}

      setPendingFiles([]);
      setUploadingImages(false);

      if (fileInputRef.current) fileInputRef.current.value = "";

      onClose();
    } catch (error) {
      console.error("Error:", error);
      setErrorMsg(error.message);
    } finally {
      setLoading(false);
      setUploadingImages(false);
    }
  };

  const isVacant = formData.status === "Vacant";

  const existingCount = (formData.images || []).length;
  const pendingCount = pendingFiles.length;
  const imageCount = existingCount + pendingCount;

  const imageCountIsValid = imageCount >= 5;

  const combinedImages = [...(formData.images || []), ...pendingFiles];

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // prevent closing while saving/uploading
        if ((loading || uploadingImages) && !nextOpen) return;

        // only close when dialog is being closed
        if (!nextOpen) onClose();
      }}>
      <DialogContent
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 sm:max-w-[700px] w-[95vw] max-h-[90vh] overflow-hidden"
        onInteractOutside={(e) => {
          if (loading || uploadingImages) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (loading || uploadingImages) e.preventDefault();
        }}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEdit ? "Edit Accommodation" : "Add New Accommodation"}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the accommodation details below."
                : "Fill out the details to add a new accommodation."}
            </DialogDescription>
          </DialogHeader>

          <div>
            <ScrollArea className="h-[60vh] pr-4 mt-2">
              <div className="space-y-6 pb-4">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="e.g. Modern Green Point Apartment"
                      required
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                      placeholder="e.g. Green Point, Cape Town"
                      required
                    />
                  </div>
                  <div>
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) =>
                        setFormData({ ...formData, capacity: e.target.value })
                      }
                      min="1"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Describe the accommodation..."
                      required
                    />
                  </div>
                </div>

                {/* Accommodation Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Type</Label>
                    <Select
                      value={formData.accommodationType || ""}
                      onValueChange={(value) =>
                        setFormData({ ...formData, accommodationType: value })
                      }>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Apartment">Apartment</SelectItem>
                        <SelectItem value="House">House</SelectItem>
                        <SelectItem value="Villa">Villa</SelectItem>
                        <SelectItem value="Group House">Group House</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Gender</Label>
                    <Select
                      value={formData.gender || ""}
                      onValueChange={(value) =>
                        setFormData({ ...formData, gender: value })
                      }>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Mixed">Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Bedrooms</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bedrooms:
                            e.target.value === ""
                              ? ""
                              : String(parseInt(e.target.value, 10) || 0),
                        })
                      }
                      required
                    />
                  </div>

                  <div>
                    <Label>Bathrooms</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      min="0"
                      value={formData.bathrooms}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bathrooms:
                            e.target.value === ""
                              ? ""
                              : String(parseInt(e.target.value, 10) || 0),
                        })
                      }
                      required
                    />
                  </div>
                </div>

                {/* Features */}
                <div>
                  <Label>Features</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PREDEFINED_FEATURES.map((feature) => (
                      <div
                        key={feature}
                        className="flex items-center space-x-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                        <Checkbox
                          id={`feature-${feature}`}
                          checked={formData.features.includes(feature)}
                          onCheckedChange={() => toggleFeature(feature)}
                        />
                        <label
                          htmlFor={`feature-${feature}`}
                          className="text-sm text-gray-700 cursor-pointer flex-1">
                          {feature}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <Label>Amenities</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PREDEFINED_AMENITIES.map((amenity) => (
                      <div
                        key={amenity}
                        className="flex items-center space-x-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                        <Checkbox
                          id={`amenity-${amenity}`}
                          checked={formData.amenities.includes(amenity)}
                          onCheckedChange={() => toggleAmenity(amenity)}
                        />
                        <label
                          htmlFor={`amenity-${amenity}`}
                          className="text-sm text-gray-700 cursor-pointer flex-1">
                          {amenity}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Images */}
                <div>
                  <Label>
                    Images{" "}
                    <span className="text-xs text-gray-500 ml-1">
                      (minimum 5)
                    </span>
                  </Label>

                  <div className="flex gap-2 mt-2 items-center">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageFilesChange}
                    />
                  </div>

                  <div
                    className={`text-xs mt-1 ${
                      imageCountIsValid ? "text-gray-500" : "text-red-500"
                    }`}>
                    {imageCount} / 5 images added
                  </div>

                  {combinedImages.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 max-w-full overflow-x-hidden">
                      {combinedImages.map((img, i) => {
                        const src = isPendingFileItem(img)
                          ? img.preview
                          : getImageSrc(img);
                        return (
                          <div
                            key={
                              isPendingFileItem(img)
                                ? img.preview
                                : typeof img === "object"
                                ? img.id ?? img.image_id ?? i
                                : img
                            }
                            className="relative border rounded p-2 bg-gray-50 w-full min-w-0 overflow-hidden">
                            <div className="w-full h-24 rounded overflow-hidden bg-gray-100 flex items-center justify-center mb-1">
                              {src ? (
                                <img
                                  src={src}
                                  alt={`Accommodation image ${i + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <ImageIcon className="h-6 w-6 text-gray-400" />
                              )}
                            </div>

                            <span className="text-[10px] text-gray-500 block truncate">
                              Image: {i + 1}
                            </span>

                            <button
                              type="button"
                              onClick={() => removeImage(img)}
                              aria-label="Remove image"
                              className="
                              absolute top-2 right-2
                              h-6 w-6
                              rounded-full
                              bg-red-500 text-white
                              flex items-center justify-center
                              shadow
                              hover:bg-red-600
                              focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2
                            ">
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-md p-6 text-center mt-3 text-gray-400">
                      No images added yet
                    </div>
                  )}
                </div>

                {/* Availability & Listing */}
                <div>
                  <Label>Availability Status</Label>

                  <div className="flex items-center justify-between mt-3 border border-gray-200 rounded-md p-3 mb-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            isVacant ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                        <p className="text-sm font-medium text-gray-800">
                          {isVacant ? "Vacant" : "Occupied"}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        Toggle to mark this accommodation as vacant or occupied.
                      </p>
                    </div>
                    <Switch
                      checked={isVacant}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: checked ? "Vacant" : "Occupied",
                        }))
                      }
                    />
                  </div>

                  <div className="border border-gray-200 rounded-md p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        Listing status
                      </p>
                      <p className="text-xs text-gray-500">
                        Control whether this accommodation is visible to
                        participants.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            listingStatus: "listed",
                          }))
                        }
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                          formData.listingStatus === "listed"
                            ? "bg-[#D2138C]/10 text-[#D2138C] border-[#D2138C]"
                            : "bg-gray-50 text-gray-600 border-gray-200"
                        }`}>
                        Listed
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            listingStatus: "unlisted",
                          }))
                        }
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                          formData.listingStatus === "unlisted"
                            ? "bg-gray-100 text-gray-800 border-gray-300"
                            : "bg-gray-50 text-gray-600 border-gray-200"
                        }`}>
                        Unlisted
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            listingStatus: "in_progress",
                          }))
                        }
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                          // ✅ FIX: was "inprogress"
                          formData.listingStatus === "in_progress"
                            ? "bg-amber-50 text-amber-700 border-amber-400"
                            : "bg-gray-50 text-gray-600 border-gray-200"
                        }`}>
                        In progress
                      </button>
                    </div>
                  </div>
                </div>

                {errorMsg && (
                  <p className="text-sm text-red-500 mt-2 whitespace-pre-line break-words">
                    {errorMsg}
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading || uploadingImages}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#D2138C] hover:bg-[#B01076] text-white"
              disabled={loading || uploadingImages}>
              {loading || uploadingImages ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isEdit ? "Updating..." : "Saving..."}
                </span>
              ) : isEdit ? (
                "Update"
              ) : (
                "Add"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AccommodationModal;
